import React, { ComponentProps } from 'react'
import { Spin } from 'antd'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'
import { Bar } from 'react-chartjs-2'
import { useCalculationSimTaskExtraTimeValues } from '../../../../api/benchmarking'
import BenchmarkingContentRow from '../../components/BenchmarkingContentRow'
import { baseGraphOptions } from '../../util/baseGraphOptions'
import { getColorByIndex } from '../../util/colors'
import { getFontSize } from '../../util/fontSize'
import { BORDER_WIDTH } from '../../util/constants'

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

const TITLE = 'Andel av total tid per insats*, övriga insatser'

export const CalculationSimTaskExtraTime: React.FC<{ text: React.ReactNode }> = ({ text }) => {
  const { data, isLoading, error } = useCalculationSimTaskExtraTimeValues()

  if (isLoading) {
    return <BenchmarkingContentRow graph={<Spin size="large" />} text={text} title={TITLE} />
  }

  if (error || !data) {
    return (
      <BenchmarkingContentRow
        title={TITLE}
        text={text}
        graph={
          <div className="flex h-full w-full items-center justify-center font-mono text-sm text-red-600">
            <span>{error ? `Fel vid hämtning av data: ${String(error)}` : 'Ingen data tillgänglig'}</span>
          </div>
        }
      />
    )
  }

  const labels = data.map((r) => r.task_name as string)
  const proportions = data.map((r) => Number(r.proportion_pct))
  const color = getColorByIndex(0)

  const datasets = [
    {
      label: 'Andel av total tid (%)',
      data: proportions,
      backgroundColor: color.fill,
      borderColor: color.border,
      borderWidth: BORDER_WIDTH,
    },
  ]

  const options: ComponentProps<typeof Bar>['options'] = {
    ...baseGraphOptions,
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const row = data[ctx.dataIndex]
            return `${ctx.parsed.y}% (${Number(row.total_hours_per_month).toFixed(1)} h/mån)`
          },
        },
      },
    },
    scales: {
      x: {
        ticks: { font: { size: getFontSize('md') } },
      },
      y: {
        min: 0,
        ticks: { font: { size: getFontSize('md') }, callback: (v) => `${v}%` },
        title: { display: true, text: 'Andel av total tid (%)', font: { size: getFontSize('md') } },
      },
    },
  }

  return <BenchmarkingContentRow title={TITLE} text={text} graph={<Bar data={{ labels, datasets }} options={options} />} />
}
